import { format } from 'date-fns/esm'
import { useCallback, useEffect, useReducer, useRef } from 'react'

import type { SlotWithLocationAndDoctor } from '../types/booking.type'

/**
 * State machine for accumulating booking slots across filter changes and
 * pagination (load-more) requests.
 *
 * ## States
 *
 * - `idle` — the accumulated slots are up-to-date with the current filters.
 *   New slots arriving in this state are **appended** (with deduplication).
 *
 * - `awaiting-filter-result` — a filter change has been initiated but the API
 *   response hasn't landed yet. The UI should treat the current slots as stale.
 *   The next batch of slots arriving in this state **replaces** the
 *   accumulation entirely instead of appending.
 *
 * ## How it's driven
 *
 * Filter detection is **event-driven**, not reactive: callers invoke
 * `markFiltersChanged()` from the handler that triggers the filter update.
 * This avoids fragile `useEffect` change-detection on serialized filters.
 *
 * Slot processing uses a single `useEffect` watching `stepSlots` and
 * `isLoading` — justified because slot data arrives as props from the parent.
 * The reducer's `status` field tells it whether to replace or append.
 *
 * The effect tracks the previous `stepSlots` reference to distinguish "new
 * slots arrived" from "isLoading toggled with the same slots". Without this,
 * an `isLoading` change would re-feed the old slots into the reducer and
 * prematurely consume the `awaiting-filter-result` state.
 */

type SlotAccumulatorStatus = 'idle' | 'awaiting-filter-result'

type SlotAccumulatorState = {
  slots: SlotWithLocationAndDoctor[]
  selectedDate: string | null
  status: SlotAccumulatorStatus
}

type SlotAccumulatorAction =
  | { type: 'FILTERS_CHANGED' }
  | { type: 'SLOTS_RECEIVED'; slots: SlotWithLocationAndDoctor[] }
  | { type: 'FILTER_SETTLED_EMPTY' }
  | { type: 'SET_SELECTED_DATE'; date: string }

const initialState: SlotAccumulatorState = {
  slots: [],
  selectedDate: null,
  status: 'idle',
}

function slotAccumulatorReducer(state: SlotAccumulatorState, action: SlotAccumulatorAction): SlotAccumulatorState {
  switch (action.type) {
    case 'FILTERS_CHANGED': {
      return { ...state, status: 'awaiting-filter-result' }
    }

    case 'SLOTS_RECEIVED': {
      if (state.status === 'awaiting-filter-result') {
        return {
          status: 'idle',
          slots: action.slots,
          selectedDate: getFirstSlotDate(action.slots),
        }
      }

      // Append with deduplication
      const existingKeys = new Set(state.slots.map(getSlotKey))
      const newSlots = action.slots.filter((slot) => !existingKeys.has(getSlotKey(slot)))
      const updatedSlots = [...state.slots, ...newSlots]

      return {
        ...state,
        status: 'idle',
        slots: updatedSlots,
        selectedDate: state.selectedDate ?? (updatedSlots.length > 0 ? getFirstSlotDate(updatedSlots) : null),
      }
    }

    case 'FILTER_SETTLED_EMPTY': {
      return { ...initialState }
    }

    case 'SET_SELECTED_DATE': {
      return { ...state, selectedDate: action.date }
    }

    default: {
      return state
    }
  }
}

// Hook
// -------------------------------------------------------------------------- /

export function useSlotAccumulator(stepSlots: SlotWithLocationAndDoctor[], isLoading: boolean) {
  const [state, dispatch] = useReducer(slotAccumulatorReducer, initialState)
  const previousStepSlotsRef = useRef<SlotWithLocationAndDoctor[] | null>(null)

  // Process incoming slots — reactive because slot data arrives as props.
  // We track the previous stepSlots reference to avoid re-processing
  // unchanged slots when isLoading toggles.
  useEffect(() => {
    const slotsChanged = previousStepSlotsRef.current !== stepSlots
    previousStepSlotsRef.current = stepSlots

    if (slotsChanged && stepSlots.length > 0) {
      dispatch({ type: 'SLOTS_RECEIVED', slots: stepSlots })
    } else if (!isLoading && state.status === 'awaiting-filter-result') {
      dispatch({ type: 'FILTER_SETTLED_EMPTY' })
    }
  }, [stepSlots, isLoading])

  const markFiltersChanged = useCallback(() => {
    dispatch({ type: 'FILTERS_CHANGED' })
  }, [])

  const setSelectedDate = useCallback((date: string) => {
    dispatch({ type: 'SET_SELECTED_DATE', date })
  }, [])

  return {
    accumulatedSlots: state.slots,
    selectedDate: state.selectedDate,
    setSelectedDate,
    isStale: state.status === 'awaiting-filter-result',
    markFiltersChanged,
  }
}

// Helpers
// -------------------------------------------------------------------------- /

function getSlotKey(slot: SlotWithLocationAndDoctor) {
  return `${slot.slotId}-${slot.start}-${slot.end}`
}

function getFirstSlotDate(slots: SlotWithLocationAndDoctor[]): string | null {
  const first = slots.find((slot) => slot.start)
  if (!first) return null
  return format(new Date(first.start), 'yyyy-MM-dd')
}
